/**
 * Edit Expense Invoice Page
 * Allows editing of existing expense invoices
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
  Button,
  Autocomplete,
  TextField,
  IconButton,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import { Delete as DeleteIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import { ExpenseInvoiceForm } from '../Components/ExpenseInvoiceForm';
import SimplifiedExpenseInvoiceForm from '../Components/SimplifiedExpenseInvoiceForm';
import { ExpenseInvoiceBookingSuggestionsPanel } from '../Components/ExpenseInvoiceBookingSuggestionsPanel';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import {
  useExpenseInvoiceQuery,
  useUpdateExpenseInvoice,
  useAssignExpenseInvoiceBookings,
  useUpdateSimplifiedExpenseInvoice,
} from '../Service/extendedExpenseInvoicesApi';
import { ExpenseInvoicesRoutes } from '../../../Router/path';
import { useNotification } from '../../Invoices/Context/NotificationContext';
import type {
  CreateExpenseInvoiceDto,
  BookingAssignmentDto,
} from '../Types/expenseInvoice.types';
import type { ExpenseInvoiceFormData } from '../Validation/expenseInvoiceValidation';
import type { SimplifiedExpenseInvoiceFormData } from '../Validation/simplifiedExpenseInvoiceValidation';
import { useGetExpenseInvoiceCompaniesLookupQuery } from '../Service/expenseInvoiceCompaniesLookupApi';
import { useGetExpenseInvoiceTagsLookupQuery } from '../Service/expenseInvoiceTagsLookupApi';
import { useGetExpenseInvoiceBookingsLookupQuery } from '../Service/expenseInvoiceBookingsLookupApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { isSimplifiedExpenseInvoiceDocument } from '../Utils/expenseInvoiceDocumentKind';
import { mapSimplifiedExpenseInvoiceFormDataToRequest } from '../Service/simplifiedExpenseInvoiceFormMapper';

export const EditExpenseInvoicePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const invoiceId = Number(id);
  const hasValidInvoiceId = Number.isFinite(invoiceId) && invoiceId > 0;

  const {
    data,
    isLoading: isFetching,
    isError: isFetchError,
  } = useExpenseInvoiceQuery(invoiceId, {
    skip: !hasValidInvoiceId,
  });
  const {
    data: allBookings,
    isLoading: bookingsLoading,
    isError: bookingsLookupError,
  } = useGetExpenseInvoiceBookingsLookupQuery();
  const [updateExpenseInvoice, fullInvoiceMutationState] = useUpdateExpenseInvoice();
  const [updateSimplifiedExpenseInvoice, simplifiedMutationState] =
    useUpdateSimplifiedExpenseInvoice();
  const [assignBookings, { isLoading: isAssigning }] = useAssignExpenseInvoiceBookings();
  const isSimplifiedDocument = isSimplifiedExpenseInvoiceDocument(data?.documentKind);
  const {
    data: companies = [],
    isLoading: companiesLoading,
    isFetching: companiesFetching,
    isError: companiesLookupError,
    error: companiesError,
    refetch: refetchCompanies,
  } =
    useGetExpenseInvoiceCompaniesLookupQuery({ skip: !isSimplifiedDocument });
  const {
    data: tags = [],
    isLoading: tagsLoading,
    isFetching: tagsFetching,
    isError: tagsLookupError,
    error: tagsError,
    refetch: refetchTags,
  } = useGetExpenseInvoiceTagsLookupQuery({
    skip: !isSimplifiedDocument,
  });

  const [assignments, setAssignments] = useState<BookingAssignmentDto[]>([]);
  const simplifiedLookupError = companiesLookupError ? companiesError : tagsLookupError ? tagsError : null;
  const simplifiedLookupErrorMessage =
    isSimplifiedDocument && simplifiedLookupError
      ? extractApiErrorMessage(
          simplifiedLookupError,
          'Nie udało się pobrać firm lub tagów dla kosztu uproszczonego.'
        )
      : null;

  useEffect(() => {
    if (data?.bookings) {
      setAssignments(
        data.bookings.map((booking) => ({
          bookingId: booking.id,
          allocatedAmount: booking.allocatedAmount,
        }))
      );
    }
  }, [data]);

  const availableBookings = useMemo(() => {
    if (!allBookings) {
      return [];
    }

    const assignedIds = new Set(assignments.map((assignment) => assignment.bookingId));
    return allBookings.filter((booking) => !assignedIds.has(booking.id));
  }, [allBookings, assignments]);

  const totalAllocated = useMemo(
    () => assignments.reduce((sum, assignment) => sum + (assignment.allocatedAmount || 0), 0),
    [assignments]
  );

  const remainingAmount = useMemo(
    () => (data?.totalGross || 0) - totalAllocated,
    [data?.totalGross, totalAllocated]
  );

  const isOverAllocated = totalAllocated > (data?.totalGross || 0);
  const hasInvalidAmounts = assignments.some((assignment) => assignment.allocatedAmount < 0);

  const handleAddBooking = (bookingId: number) => {
    setAssignments((previousAssignments) => [
      ...previousAssignments,
      { bookingId, allocatedAmount: 0 },
    ]);
  };

  const handleRemoveBooking = (bookingId: number) => {
    setAssignments((previousAssignments) =>
      previousAssignments.filter((assignment) => assignment.bookingId !== bookingId)
    );
  };

  const handleUpdateAmount = (bookingId: number, amount: number) => {
    setAssignments((previousAssignments) =>
      previousAssignments.map((assignment) =>
        assignment.bookingId === bookingId ? { ...assignment, allocatedAmount: amount } : assignment
      )
    );
  };

  const handleSaveAssignments = async () => {
    try {
      await assignBookings({
        invoiceId,
        assignments,
      }).unwrap();

      showSuccess('Bookingi zostały przypisane pomyślnie');
    } catch (err) {
      showError(extractApiErrorMessage(err, 'Błąd podczas przypisywania bookingów'));
    }
  };

  const defaultValues: Partial<ExpenseInvoiceFormData> | undefined = useMemo(() => {
    if (!data || isSimplifiedDocument) {
      return undefined;
    }

    return {
      invoiceNumber: data.invoiceNumber ?? data.documentReference,
      issueDate: data.issueDate,
      saleDate: data.saleDate,
      paymentDueDate: data.paymentDueDate,
      supplierName: data.supplierName ?? '',
      supplierNip: data.supplierNip,
      supplierAddress: data.supplierAddress,
      supplierBankAccount: data.supplierBankAccount,
      companyId: data.companyId,
      currency: data.currency,
      status: data.status,
      notes: data.notes,
      items: (data.items ?? []).map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unitNet: item.unitNet,
        unitGross: item.unitGross,
        vatRate: item.vatRate,
        isVatExempt: item.isVatExempt,
        vatExemptNote: item.vatExemptNote,
      })),
    };
  }, [data, isSimplifiedDocument]);

  const simplifiedDefaultValues: Partial<SimplifiedExpenseInvoiceFormData> | undefined = useMemo(() => {
    if (!data || !isSimplifiedDocument) {
      return undefined;
    }

    return {
      companyId: data.companyId,
      issueDate: data.issueDate,
      totalGross: data.totalGross,
      currency: data.currency,
      description: data.description ?? '',
      tagIds: data.tags.map((tag) => tag.id),
    };
  }, [data, isSimplifiedDocument]);

  const simplifiedCompanies = useMemo(() => {
    if (!isSimplifiedDocument) {
      return companies;
    }

    const normalizedCompanies = [...companies];

    if (
      data?.companyId !== null &&
      data?.companyId !== undefined &&
      !normalizedCompanies.some((company) => company.id === data.companyId)
    ) {
      normalizedCompanies.push({
        id: data.companyId,
        name: data.companyName ?? `Firma #${data.companyId}`,
      });
    }

    return normalizedCompanies.sort((left, right) => left.name.localeCompare(right.name, 'pl'));
  }, [companies, data, isSimplifiedDocument]);

  const simplifiedTags = useMemo(() => {
    if (!isSimplifiedDocument) {
      return tags;
    }

    const normalizedTags = [...tags];

    for (const tag of data?.tags ?? []) {
      if (!normalizedTags.some((existingTag) => existingTag.id === tag.id)) {
        normalizedTags.push({ id: tag.id, name: tag.name });
      }
    }

    return normalizedTags.sort((left, right) => left.name.localeCompare(right.name, 'pl'));
  }, [data, isSimplifiedDocument, tags]);

  const handleRetryLookups = () => {
    void Promise.all([refetchCompanies(), refetchTags()]);
  };

  const handleSubmit = async (formData: CreateExpenseInvoiceDto) => {
    try {
      await updateExpenseInvoice({
        id: invoiceId,
        data: formData,
      }).unwrap();

      showSuccess(`Faktura ${formData.invoiceNumber} została zaktualizowana`);
      navigate(ExpenseInvoicesRoutes.details.replace(':id', String(invoiceId)));
    } catch (err) {
      showError(
        extractApiErrorMessage(
          err,
          'Nie udało się zaktualizować faktury. Sprawdź formularz i spróbuj ponownie.'
        )
      );
    }
  };

  const handleSimplifiedSubmit = async (formData: SimplifiedExpenseInvoiceFormData) => {
    try {
      await updateSimplifiedExpenseInvoice({
        id: invoiceId,
        data: mapSimplifiedExpenseInvoiceFormDataToRequest(formData),
      }).unwrap();

      showSuccess(`Koszt uproszczony ${data?.documentReference ?? invoiceId} został zaktualizowany`);
      navigate(ExpenseInvoicesRoutes.details.replace(':id', String(invoiceId)));
    } catch (err) {
      showError(
        extractApiErrorMessage(
          err,
          'Nie udało się zaktualizować kosztu uproszczonego. Sprawdź formularz i spróbuj ponownie.'
        )
      );
    }
  };

  const handleCancel = () => {
    navigate(ExpenseInvoicesRoutes.details.replace(':id', String(invoiceId)));
  };

  if (!hasValidInvoiceId) {
    return (
      <Box p={3}>
        <Alert severity="error">Nieprawidłowy identyfikator dokumentu kosztowego.</Alert>
      </Box>
    );
  }

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400} p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (isFetchError || !data) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Nie udało się załadować dokumentu kosztowego. Sprawdź czy dokument istnieje.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {fullInvoiceMutationState.isError && !isSimplifiedDocument && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {extractApiErrorMessage(
            fullInvoiceMutationState.error,
            'Nie udało się zaktualizować faktury. Spróbuj ponownie.'
          )}
        </Alert>
      )}

      {simplifiedMutationState.isError && isSimplifiedDocument && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {extractApiErrorMessage(
            simplifiedMutationState.error,
            'Nie udało się zaktualizować kosztu uproszczonego. Spróbuj ponownie.'
          )}
        </Alert>
      )}

      {isSimplifiedDocument ? (
        <SimplifiedExpenseInvoiceForm
          mode="edit"
          defaultValues={simplifiedDefaultValues}
          companies={simplifiedCompanies}
          tags={simplifiedTags}
          companiesLoading={companiesLoading || companiesFetching}
          tagsLoading={tagsLoading || tagsFetching}
          lookupErrorMessage={simplifiedLookupErrorMessage}
          onRetryLookups={handleRetryLookups}
          onSubmit={handleSimplifiedSubmit}
          onCancel={handleCancel}
          isLoading={simplifiedMutationState.isLoading}
        />
      ) : (
        <ExpenseInvoiceForm
          mode="edit"
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={fullInvoiceMutationState.isLoading}
        />
      )}

      <AccordionCard title="Przypisane Bookingi" icon={<AssignmentIcon />} defaultExpanded={false}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {assignments.length > 0 ? (
            assignments.map((assignment) => {
              // The lookup list hides cancelled bookings, so fall back to the invoice's own summary.
              const booking =
                allBookings?.find((candidate) => candidate.id === assignment.bookingId) ??
                data?.bookings?.find((candidate) => candidate.id === assignment.bookingId);
              // A failed lookup says nothing about the booking itself, so keep the label neutral.
              const missingBookingLabel = bookingsLookupError
                ? ''
                : bookingsLoading
                  ? 'Ładowanie...'
                  : '(anulowany lub niedostępny)';
              const bookingLabel = booking?.offerName || missingBookingLabel;

              return (
                <Box
                  key={assignment.bookingId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold">
                      Booking #{assignment.bookingId}
                      {bookingLabel ? ` - ${bookingLabel}` : ''}
                    </Typography>
                    {booking && (
                      <Typography variant="body2" color="text.secondary">
                        {booking.bookingDateFrom} → {booking.bookingDateTo}
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    type="number"
                    label="Przypisana kwota"
                    value={assignment.allocatedAmount || ''}
                    onChange={(event) =>
                      handleUpdateAmount(assignment.bookingId, parseFloat(event.target.value) || 0)
                    }
                    size="small"
                    sx={{ width: 180 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{data.currency}</InputAdornment>,
                    }}
                    inputProps={{
                      step: 0.01,
                    }}
                    error={false}
                    helperText={''}
                  />

                  <IconButton
                    onClick={() => handleRemoveBooking(assignment.bookingId)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary">
              Brak przypisanych bookingów
            </Typography>
          )}

          {assignments.length > 0 && (
            <Box
              sx={{
                p: 2,
                backgroundColor: isOverAllocated ? 'error.light' : 'success.light',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight="bold">
                  Kwota dokumentu:
                </Typography>
                <Typography variant="body2">
                  {(data.totalGross || 0).toFixed(2)} {data.currency}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight="bold">
                  Suma przypisanych kwot:
                </Typography>
                <Typography
                  variant="body2"
                  color={isOverAllocated ? 'error.main' : 'text.primary'}
                  fontWeight={isOverAllocated ? 'bold' : 'normal'}
                >
                  {totalAllocated.toFixed(2)} {data.currency}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight="bold">
                  Pozostało do rozdziału:
                </Typography>
                <Typography
                  variant="body2"
                  color={remainingAmount < 0 ? 'error.main' : 'success.main'}
                  fontWeight={remainingAmount !== 0 ? 'bold' : 'normal'}
                >
                  {remainingAmount.toFixed(2)} {data.currency}
                </Typography>
              </Box>

              {isOverAllocated && (
                <Typography variant="caption" color="error.main" sx={{ mt: 1 }}>
                  ⚠️ Suma przypisanych kwot przekracza wartość dokumentu.
                </Typography>
              )}

              {hasInvalidAmounts && (
                <Typography variant="caption" color="error.main" sx={{ mt: 1 }}>
                  ⚠️ Kwoty przypisań nie mogą być ujemne.
                </Typography>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Przydział kosztów: {((totalAllocated / (data.totalGross || 1)) * 100).toFixed(1)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((totalAllocated / (data.totalGross || 1)) * 100, 100)}
                  color={isOverAllocated ? 'error' : 'primary'}
                  sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                />
              </Box>
            </Box>
          )}

          <ExpenseInvoiceBookingSuggestionsPanel
            expenseInvoiceId={invoiceId}
            assignedBookingIds={assignments.map((assignment) => assignment.bookingId)}
            onAddBooking={handleAddBooking}
          />

          <Autocomplete
            options={availableBookings}
            getOptionLabel={(option) =>
              `#${option.id} - ${option.offerName || 'Brak nazwy'} (${option.bookingDateFrom} → ${
                option.bookingDateTo
              })`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Dodaj booking"
                placeholder="Szukaj po ID, nazwie oferty lub datach..."
              />
            )}
            onChange={(_, value) => {
              if (value) {
                handleAddBooking(value.id);
              }
            }}
            loading={bookingsLoading}
            disabled={bookingsLoading || isAssigning}
            value={null}
          />

          <Button
            variant="contained"
            onClick={handleSaveAssignments}
            disabled={isAssigning || isOverAllocated || hasInvalidAmounts}
            fullWidth
          >
            {isAssigning ? <CircularProgress size={24} /> : 'Zapisz przypisania'}
          </Button>
        </Box>
      </AccordionCard>
    </Box>
  );
};

export default EditExpenseInvoicePage;
